import React, { useState } from "react";
import { 
	Box, 
	Typography, 
	TextField,
	Checkbox,
	FormControlLabel,
	FormGroup,
	IconButton,
	Tooltip,
	Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { styled } from "@mui/material/styles";
import SignatureModal from "../SignatureModal/SignatureModal";
import { deriveInitialsFromName } from "../../utils/initials";

const FormContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),
	padding: theme.spacing(2),
	backgroundColor: "white",
	borderRadius: theme.shape.borderRadius,
	boxShadow: theme.shadows[1],
}));

const FieldContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1),
}));

const SignatureField = styled(Box)(({ theme }) => ({
	border: `1px solid ${theme.palette.grey[300]}`,
	borderRadius: theme.shape.borderRadius,
	padding: theme.spacing(1.5),
	minHeight: "56px",
	flexGrow: 1,
	display: "flex",
	alignItems: "center",
	cursor: "pointer",
	backgroundColor: theme.palette.grey[50],
	"&:hover": {
		backgroundColor: theme.palette.grey[100],
		borderColor: theme.palette.primary.main,
	},
}));

const SignaturePlaceholder = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontStyle: "italic",
}));

const SignatureContent = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: "100%",
	height: "100%",
}));

interface ValueField {
	id: string;
	label: string;
	placeholder: string;
	value: string;
	fieldType: string;
	autoGenerateInitials?: boolean;
}

interface DynamicFormProps {
	values: ValueField[];
	onChange: (fieldId: string, value: string, fieldType?: string) => void;
	onBlur?: (fieldId: string, value: string, fieldType?: string) => void;
	onFocus?: (fieldId: string) => void;
	activeFieldId?: string | null;
	signatureImage: string | null;
	onSaveSignature?: (signatureData: string) => void;
	/** Used to suggest initials (e.g. from profile name) */
	signerDisplayName?: string;
}

const DynamicForm: React.FC<DynamicFormProps> = ({ values, onChange, onBlur, onFocus, activeFieldId, signatureImage, onSaveSignature, signerDisplayName }) => {
	const [signatureModalOpen, setSignatureModalOpen] = useState(false);
	const [currentSignatureField, setCurrentSignatureField] = useState<string | null>(null);

	const handleFocus = (fieldId: string) => {
		if (onFocus) {
			onFocus(fieldId);
		}
	};

	const handleSignatureClick = (fieldId: string) => {
		handleFocus(fieldId);
		setCurrentSignatureField(fieldId);
		setSignatureModalOpen(true);
	};

	const handleSignatureUse = (signatureData: string) => {
		if (currentSignatureField !== null && currentSignatureField !== undefined) {
			const field = values.find(f => f.id === currentSignatureField);
			const fieldType = field?.fieldType || 'signature';
			onChange(currentSignatureField, signatureData, fieldType);
			if (onBlur) {
				onBlur(currentSignatureField, signatureData, fieldType);
			}
		}
	};

	const handleSignatureClose = () => {
		setSignatureModalOpen(false);
		setCurrentSignatureField(null);
	};

	const renderField = (field: ValueField) => {
		const { id, fieldType, label, placeholder, value } = field;

		const handleChange = (
			event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
		) => {
			const target = event.target as HTMLInputElement;
			const newValue =
				target.type === "checkbox"
					? target.checked.toString()
					: target.value;
			onChange(id, newValue, fieldType);
		};

		const handleBlur = (
			event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
		) => {
			if (onBlur) {
				onBlur(id, value, fieldType);
			}
		};

		switch (fieldType) {
			case "shortText":
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							{label}
						</Typography>
						<TextField
							variant="outlined"
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							onFocus={() => handleFocus(id)}
							fullWidth
							placeholder={placeholder}
						/>
					</Box>
				);
			case "checkbox":
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							{label}
						</Typography>
						<FormGroup>
							<FormControlLabel
								label=""
								control={
									<Checkbox
										checked={value === "true"}
										onChange={e => {
											const newValue = e.target.checked ? "true" : "false";
											onChange(id, newValue, fieldType);
											if (onBlur) onBlur(id, newValue, fieldType);
										}}
										onFocus={() => handleFocus(id)}
										color="primary"
									/>
								}
							/>
						</FormGroup>
					</Box>
				);
			case "date":
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							{label}
						</Typography>
						<TextField
							variant="outlined"
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							onFocus={() => handleFocus(id)}
							fullWidth
							placeholder={placeholder || "MM/DD/YYYY"}
							inputProps={{
								inputMode: "numeric",
								pattern: String.raw`\d{2}\/\d{2}\/\d{4}`,
							}}
						/>
					</Box>
				);
			case "initials": {
				const suggested = deriveInitialsFromName(signerDisplayName || "");
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							{label || "Initials"}
						</Typography>
						<TextField
							variant="outlined"
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							onFocus={() => handleFocus(id)}
							fullWidth
							placeholder={placeholder || "e.g. JD"}
							inputProps={{ maxLength: 12 }}
						/>
						{suggested ? (
							<Box mt={1}>
								<Button
									size="small"
									variant="outlined"
									color="primary"
									onClick={() => {
										onChange(id, suggested, fieldType);
										if (onBlur) onBlur(id, suggested, fieldType);
									}}
								>
									Use initials from my name ({suggested})
								</Button>
							</Box>
						) : null}
					</Box>
				);
			}
			case "text":
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							{label}
						</Typography>
						<TextField
							variant="outlined"
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							onFocus={() => handleFocus(id)}
							fullWidth
							placeholder={placeholder}
						/>
					</Box>
				);
			case "select":
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							{label}
						</Typography>
						<TextField
							select
							variant="outlined"
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							onFocus={() => handleFocus(id)}
							fullWidth
							placeholder={placeholder}
						/>
					</Box>
				);

			case "signature":
				return (
					<Box>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
							 Signature
						</Typography>
						<Box display="flex" alignItems="center" gap={2}>
							<SignatureField 
								onClick={() => handleSignatureClick(id)}
							>
								{value ? (
									<SignatureContent>
										<img 
											src={value} 
											alt="Signature" 
											style={{ 
												maxWidth: "100%", 
												maxHeight: "40px", 
												objectFit: "contain" 
											}} 
										/>
									</SignatureContent>
								) : (
									<SignaturePlaceholder variant="body2">
										Click to add your signature
									</SignaturePlaceholder>
								)}
							</SignatureField>
							{value && (
								<Tooltip title="Remove signature">
									<IconButton
										size="small"
										onClick={() => {
											const currentFieldType = fieldType || "signature";
											onChange(id, "", currentFieldType);
											if (onBlur) {
												onBlur(id, "", currentFieldType);
											}
										}}
										aria-label="Remove signature"
									>
										<DeleteOutlineIcon fontSize="small" htmlColor="#616161" />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					</Box>
				);
			default:
				return (
					<Box>
                        <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
                            {label}
						</Typography>
						<TextField
							variant="outlined"
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							onFocus={() => handleFocus(id)}
							fullWidth
							placeholder={placeholder}
						/>
					</Box>
				);
		}
	};

	return (
		<>
			<FormContainer>
				{values.map((field) => (
					<FieldContainer key={field.id}>
						{renderField(field)}
					</FieldContainer>
				))}
			</FormContainer>

			{/* Modal para agregar firma */}
			<SignatureModal
				open={signatureModalOpen}
				onClose={handleSignatureClose}
				onSaveSignature={onSaveSignature}
				onUseSignature={handleSignatureUse}
				signatureImage={signatureImage}
			/>
		</>
	);
};

export default DynamicForm;
